import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import SearchBar from "@/components/SearchBar";
import BrandMark from "@/components/BrandMark";
import {
  LogOut, Shield, LayoutDashboard, Settings, BookOpen, Sparkles,
  Menu, X, IdCard, Home,
} from "lucide-react";

export default function Navbar({ showSearch = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the drawer on route change and lock scroll while open.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.documentElement.style.overflow = mobileOpen ? "hidden" : "";
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.documentElement.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [mobileOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Every route we surface in the mobile drawer. Only entries relevant to
  // the current user role are rendered.
  const mobileLinks = user
    ? [
        { to: "/dashboard", label: "Dashboard",  icon: LayoutDashboard },
        { to: "/royai",     label: "Features",   icon: Sparkles, gold: true },
        { to: "/passport",  label: "Passport",   icon: IdCard },
        { to: "/courses",   label: "Courses",    icon: BookOpen },
        { to: "/account",   label: "Account",    icon: Settings },
        ...(user.role === "admin"
          ? [{ to: "/admin", label: "Admin", icon: Shield }]
          : []),
      ]
    : [
        { to: "/",       label: "Home",     icon: Home },
        { to: "/courses", label: "Courses", icon: BookOpen },
        { to: "/#royai", label: "Features",   icon: Sparkles, gold: true },
      ];

  return (
    <header className="sticky top-0 z-50 backdrop-blur-2xl bg-black/70 border-b border-white/10">
      <div className="px-4 sm:px-6 md:px-10 lg:px-16 py-4 flex items-center gap-3 md:gap-6">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-3 shrink-0" data-testid="brand-link">
          <BrandMark size={48} />
          <span className="font-display font-extrabold text-xl tracking-tight">BeatCredsi<span className="text-amber-500 text-xs align-super">™</span></span>
          <span className="hidden md:inline text-overline text-neutral-500">Music Infrastructure Intelligence</span>
        </Link>

        {showSearch && (
          <div className="flex-1 max-w-2xl hidden md:block">
            <SearchBar />
          </div>
        )}
        {!showSearch && <div className="flex-1" />}

        {/* Desktop navigation — hidden on mobile */}
        <nav className="hidden md:flex items-center gap-2 md:gap-4">
          {user ? (
            <>
              {user.role === "admin" && location.pathname !== "/admin" && (
                <Link to="/admin" data-testid="admin-link"
                  className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-amber-500 transition-colors">
                  <Shield size={16} /> Admin
                </Link>
              )}
              {location.pathname !== "/dashboard" && (
                <Link to="/dashboard" data-testid="dashboard-link"
                  className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-amber-500 transition-colors">
                  <LayoutDashboard size={16} /> Dashboard
                </Link>
              )}
              {!location.pathname.startsWith("/courses") && (
                <Link to="/courses" data-testid="nav-courses"
                  className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-amber-500 transition-colors">
                  <BookOpen size={16} /> Courses
                </Link>
              )}
              {location.pathname !== "/royai" && (
                <Link to="/royai" data-testid="nav-royai"
                  className="inline-flex items-center gap-2 text-sm text-amber-500 hover:text-amber-400 transition-colors">
                  <Sparkles size={16} /> Features
                </Link>
              )}
              {location.pathname !== "/account" && (
                <Link to="/account" data-testid="nav-account"
                  className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-amber-500 transition-colors">
                  <Settings size={16} /> Account
                </Link>
              )}
              {location.pathname !== "/plans" && (
                <Link to="/plans" data-testid="nav-plans"
                  className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-amber-500 transition-colors">
                  Plans
                </Link>
              )}
              {location.pathname !== "/vault" && (
                <Link to="/vault" data-testid="nav-vault"
                  className="inline-flex items-center gap-2 text-sm text-emerald-400 hover:text-emerald-300 transition-colors">
                  Vault
                </Link>
              )}
              <div className="flex items-center gap-2 px-3 py-1.5 border border-white/10 bg-[#121212]">
                <div className="w-2 h-2 bg-amber-500" />
                <span className="text-xs text-neutral-300 truncate max-w-[160px]">{user.email}</span>
              </div>
              <button onClick={handleLogout} data-testid="logout-btn"
                className="inline-flex items-center gap-2 px-3 py-2 text-sm text-neutral-300 hover:text-white border border-transparent hover:border-white/20 transition-colors">
                <LogOut size={16} /> Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/plans" data-testid="nav-plans-guest" className="text-sm text-neutral-300 hover:text-amber-500 px-3 py-2">Plans</Link>
              <Link to="/login" data-testid="nav-login" className="text-sm text-neutral-300 hover:text-amber-500 px-3 py-2">Login</Link>
              <Link to="/signup" data-testid="nav-signup"
                className="text-sm font-semibold px-4 py-2 bg-amber-500 text-black hover:brightness-110 transition-all">
                Get My Score
              </Link>
            </>
          )}
        </nav>

        {/* Mobile — Menu / hamburger button */}
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          className="md:hidden inline-flex items-center gap-2 border border-amber-500/40 bg-black/60 text-amber-400 hover:bg-amber-500/10 px-3 py-2 transition-colors"
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          <span className="font-mono-tech text-[11px] uppercase tracking-[0.22em] font-semibold">
            {mobileOpen ? "Close" : "Menu"}
          </span>
        </button>
      </div>

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <>
          <div
            onClick={() => setMobileOpen(false)}
            className="md:hidden fixed inset-0 top-[73px] bg-black/70 backdrop-blur-sm z-40"
            data-testid="mobile-menu-backdrop"
          />
          <div
            className="md:hidden fixed left-3 right-3 top-[80px] z-50 border border-amber-500/30 bg-[#0A0A0A] shadow-[0_20px_80px_-20px_rgba(212,175,55,0.35)]"
            role="dialog"
            aria-modal="true"
            data-testid="mobile-menu-drawer"
          >
            {/* Header inside the drawer */}
            <div className="px-4 py-3 border-b border-white/10 bg-black/60 flex items-center justify-between">
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-amber-400">
                {user ? "Signed in" : "Menu"}
              </span>
              {user && (
                <span className="font-mono-tech text-[10px] text-neutral-400 truncate max-w-[60%]">
                  {user.email}
                </span>
              )}
            </div>

            <ul className="p-3 space-y-1">
              {mobileLinks
                .filter((l) => l.to !== location.pathname)
                .map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 border transition-colors
                        ${l.gold
                          ? "border-amber-500/40 bg-amber-500/[0.06] text-amber-300 hover:bg-amber-500/[0.12]"
                          : "border-white/5 bg-black/40 text-neutral-200 hover:border-amber-500/40 hover:text-amber-300"}`}
                      data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-").replace(/™/g, "")}`}
                    >
                      <l.icon size={16} className={l.gold ? "text-amber-400" : "text-neutral-400"} />
                      <span className="font-mono-tech text-xs uppercase tracking-[0.22em] font-semibold flex-1">{l.label}</span>
                      <span className={`font-mono-tech text-lg leading-none ${l.gold ? "text-amber-400" : "text-neutral-500"}`} aria-hidden>›</span>
                    </Link>
                  </li>
                ))}
            </ul>

            <div className="p-3 pt-0 border-t border-white/10 mt-1">
              {user ? (
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 border border-rose-500/40 bg-rose-500/[0.05] text-rose-300 hover:bg-rose-500/10 px-4 py-3 font-mono-tech text-xs uppercase tracking-[0.22em] font-semibold transition-colors"
                  data-testid="mobile-logout"
                >
                  <LogOut size={14} /> Sign out
                </button>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Link
                    to="/login"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-center border border-white/10 bg-black/60 text-neutral-200 hover:text-amber-300 hover:border-amber-500/40 px-3 py-3 font-mono-tech text-xs uppercase tracking-[0.22em] font-semibold transition-colors"
                    data-testid="mobile-login"
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-center bg-amber-500 text-black hover:brightness-110 px-3 py-3 font-mono-tech text-xs uppercase tracking-[0.22em] font-semibold transition-all"
                    data-testid="mobile-signup"
                  >
                    Get My Score
                  </Link>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </header>
  );
}
